import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FaPencilAlt,
  FaPaperPlane,
  FaSpinner,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const GERMAN_LEVELS = [
  { value: "A1", label: "A1 - Beginner" },
  { value: "A2", label: "A2 - Elementary" },
  { value: "B1", label: "B1 - Intermediate" },
  { value: "B2", label: "B2 - Upper Intermediate" },
];

const SchreibenPracticePage: React.FC = () => {
  const { student } = useAuth();

  const [level, setLevel] = useState<string>("");
  const [topic, setTopic] = useState<string>("");
  const [writing, setWriting] = useState<string>("");
  const [correction, setCorrection] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setCorrection("");

    // Validation
    if (!level) {
      setError("Please select your German language level");
      return;
    }
    if (!writing.trim()) {
      setError("Please write something to get corrections");
      return;
    }
    if (writing.trim().length < 20) {
      setError("Please write at least 20 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/writing/correct`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            level,
            topic: topic.trim() || null,
            writing: writing.trim(),
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setCorrection(data.correction);
      } else {
        setError(data.error || "Failed to get corrections. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting writing:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setLevel("");
    setTopic("");
    setWriting("");
    setCorrection("");
    setError("");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaPencilAlt />}
        title="Writing Practice (Schreiben)"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Writing Practice" },
        ]}
      />

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Instructions Card */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            কিভাবে কাজ করে (How it works)
          </h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">১.</span>
              <span>
                আপনার German language level নির্বাচন করুন (A1 থেকে C2)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">২.</span>
              <span>
                (ঐচ্ছিক) আপনি কি বিষয়ে লিখছেন তা উল্লেখ করুন (topic, theme,
                ইত্যাদি)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">৩.</span>
              <span>Textbox-এ আপনার German লেখা লিখুন</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">৪.</span>
              <span>
                Submit করুন এবং আপনার level অনুযায়ী corrections ও optimized
                version পান
              </span>
            </li>
          </ul>
          <div className="mt-4 bg-white/50 rounded-lg p-3 border border-blue-200">
            <p className="text-sm text-gray-800 flex items-start gap-2">
              <FaCheckCircle className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>বিশেষ বৈশিষ্ট্য:</strong> সমস্ত corrections German
                ভাষায় দেওয়া হবে এবং ব্যাখ্যা বাংলায় থাকবে (প্রয়োজনে English
                শব্দ সহ)। Optimized version শুধুমাত্র German ভাষায় থাকবে।
              </span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaPencilAlt className="text-[#009fe3]" />
              আপনার লেখা (Your Writing)
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Level Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  German Language Level * (আপনার লেভেল)
                </label>
                <select
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all text-sm"
                  style={{ fontSize: "16px" }}
                  required
                >
                  <option value="">Select your level</option>
                  {GERMAN_LEVELS.map((lvl) => (
                    <option key={lvl.value} value={lvl.value}>
                      {lvl.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Topic Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Topic / Context (ঐচ্ছিক - Optional)
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="উদাহরণ: Meine Familie, Mein Tagesablauf, Mein Hobby..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all text-sm"
                  style={{ fontSize: "16px" }}
                />
              </div>

              {/* Writing Textarea */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  আপনার German লেখা * (Your German Writing)
                </label>
                <textarea
                  value={writing}
                  onChange={(e) => setWriting(e.target.value)}
                  placeholder="এখানে আপনার German text লিখুন... (Write your German text here...)"
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all text-sm resize-none"
                  style={{ fontSize: "16px" }}
                  required
                />
                <div className="text-xs text-gray-500 mt-1">
                  {writing.length} characters (minimum ২০টি character প্রয়োজন)
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-0.5 disabled:transform-none"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Correction পান
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={loading}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-green-600" />
              Corrections & Feedback
            </h2>

            {correction ? (
              <div className="prose prose-sm max-w-none">
                <div
                  className="text-sm text-gray-800 leading-relaxed"
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {correction.split("\n").map((line, index) => {
                    // Bold text formatting
                    if (line.includes("**")) {
                      const parts = line.split(/(\*\*.*?\*\*)/g);
                      return (
                        <p key={index} className="mb-2">
                          {parts.map((part, i) => {
                            if (part.startsWith("**") && part.endsWith("**")) {
                              return (
                                <strong key={i} className="text-gray-900">
                                  {part.slice(2, -2)}
                                </strong>
                              );
                            }
                            return <span key={i}>{part}</span>;
                          })}
                        </p>
                      );
                    }
                    // Regular lines
                    if (line.trim()) {
                      return (
                        <p key={index} className="mb-2">
                          {line}
                        </p>
                      );
                    }
                    // Empty lines
                    return <br key={index} />;
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <FaPencilAlt className="text-5xl mb-4" />
                <p className="text-center text-sm">
                  আপনার লেখা submit করুন corrections এবং feedback দেখতে
                </p>
                <p className="text-center text-xs mt-2">
                  Submit your writing to see corrections and feedback here
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SchreibenPracticePage;
